<script setup lang="ts">
import { BadgePaymentService, BadgeTutorStatus } from '@shared/ui/badges'
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'
import { BtnCheckbox } from '@shared/ui/buttons'

import { useTableTutorBilling } from '../model/table-tutor-billing.hook'
import { useTutorBillingSelection } from '../model/tutor-billing-selection.hook'
import TutorBillingIndicator from './TutorBillingIndicator.vue'
import TutorBillingRowActions from './TutorBillingRowActions.vue'
import TutorBillingStatusFlags from './TutorBillingStatusFlags.vue'

defineOptions({
  name: 'TableTutorBilling',
})

const {
  rows,
  isLoading,
  hasRows,
  meta,
  perPageOptions,
  setChangePage,
  setChangePerPage,
  getTutorName,
  getTutorUrl,
  getAccountHolder,
  getAccount,
  getCountry,
  setOpenView,
} = useTableTutorBilling()

const {
  isAllSelected,
  summary,
  isSelected,
  setToggleRow,
  setToggleAll,
} = useTutorBillingSelection()
</script>

<template>
  <div class="table-tutor-billing">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #summary>
        <div class="table-tutor-billing__summary">
          <AppText
            name="body2"
            color="slate-700"
          >
            {{ summary }}
          </AppText>
        </div>
      </template>

      <template #head>
        <AppTr>
          <AppTh class="table-tutor-billing__checkbox-cell">
            <BtnCheckbox
              :checked="isAllSelected"
              :disabled="!hasRows"
              label="Select all records on this page"
              @click="setToggleAll"
            />
          </AppTh>
          <AppTh class="text-center table-tutor-billing__id-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              #
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Tutor
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Status
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Email
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Payment service
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Account owner name
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Account number / Email
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Country
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Legal status
            </AppText>
          </AppTh>
          <AppTh class="table-tutor-billing__actions-cell" />
        </AppTr>
      </template>

      <template #body>
        <AppTr
          v-for="row in rows"
          :key="row.id"
          :selected="isSelected(row)"
        >
          <AppTd class="medium table-tutor-billing__checkbox-cell">
            <BtnCheckbox
              :checked="isSelected(row)"
              :label="`Select billing record ${row.id}`"
              @click="setToggleRow(row)"
            />
          </AppTd>
          <AppTd class="medium text-center table-tutor-billing__id-cell">
            <button
              type="button"
              class="table-tutor-billing__cell-link"
              :aria-label="`Open billing #${row.id}`"
              @click="setOpenView(row)"
            >
              <AppText
                name="body1"
                color="blue-600"
              >
                {{ row.id }}
              </AppText>
            </button>
          </AppTd>
          <AppTd class="medium">
            <div class="table-tutor-billing__tutor">
              <LinkTextPrimary
                :href="getTutorUrl(row)"
                target="_blank"
              >
                {{ getTutorName(row) }}
              </LinkTextPrimary>
              <TutorBillingIndicator :row="row" />
            </div>
          </AppTd>
          <AppTd class="medium">
            <BadgeTutorStatus
              v-if="row.tutor.status"
              :status="row.tutor.status"
            />
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              —
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ row.tutor.email }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <BadgePaymentService :service="row.payment.payment_service" />
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getAccountHolder(row) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getAccount(row) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ getCountry(row) }}
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <TutorBillingStatusFlags :row="row" />
          </AppTd>
          <AppTd class="zero-padding table-tutor-billing__actions-cell">
            <TutorBillingRowActions :row="row" />
          </AppTd>
        </AppTr>

        <AppTr v-if="!hasRows && !isLoading">
          <AppTd
            class="text-center table-tutor-billing__empty-cell"
            :colspan="11"
          >
            <AppText
              name="body1"
              color="slate-500"
            >
              No billing records found
            </AppText>
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableTutorBilling.scss" />
